/* eslint-disable max-lines */
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { mergeMap, of, sampleTime, switchMap } from 'rxjs';
import { buffer, catchError, map, withLatestFrom } from 'rxjs/operators';

import { CurrentUserSelectors, UserActions, UserSelectors } from '@recruitee/account-data';
import type { FullCandidate } from '@recruitee/candidate-types';
import { filterDefined } from '@recruitee/common';
import { referencesToActionsFactory } from '@recruitee/ngrx';
import { JobsBriefActions, StageActions } from '@recruitee/offers-data';
import { PlacementActions } from '@recruitee/placements-data';
import { CandidateReferrerActions } from '@recruitee/referrals-data';
import { TalentPoolsNewBriefActions } from '@recruitee/talent-pools-data';
import { uniqByKey } from '@recruitee/utils';

import { FullCandidateHttpClient, FullCandidateReferences } from './full-candidate-http-client';
import { FullCandidateActions } from './full-candidate.store';

@Injectable({ providedIn: 'root' })
export class FullCandidateEffects {
  public fetchOne$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FullCandidateActions.fetchOne),
      switchMap(({ payload }) =>
        this.fullCandidateHttpClient.fetchOne(payload.id).pipe(
          switchMap(({ candidate, references }) => [
            ...this.getReferencesActions(candidate, references),
            FullCandidateActions.fetchOneSuccess({ payload: { data: candidate } }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(FullCandidateActions.fetchOneFailed({ payload: { error, id: payload.id } })),
          ),
        ),
      ),
    ),
  );

  public bufferedFetchOne$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FullCandidateActions.bufferedFetchOne),
      map(({ payload }) => payload.id),
      buffer(
        this.actions$.pipe(ofType(FullCandidateActions.bufferedFetchOne)).pipe(sampleTime(100)),
      ),
      mergeMap(ids => [...new Set(ids)]),
      mergeMap(id =>
        this.fullCandidateHttpClient.fetchOne(id).pipe(
          switchMap(({ candidate, references }) => [
            ...this.getReferencesActions(candidate, references),
            FullCandidateActions.fetchOneSuccess({ payload: { data: candidate } }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(FullCandidateActions.fetchOneFailed({ payload: { error, id } })),
          ),
        ),
      ),
    ),
  );

  public create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FullCandidateActions.createOnePessimistic),
      switchMap(({ payload }) =>
        this.fullCandidateHttpClient.create(payload.params).pipe(
          switchMap(({ candidate, references }) => [
            ...this.getReferencesActions(candidate, references),

            FullCandidateActions.createOnePessimisticSuccess({
              payload: { data: candidate, correlationId: payload.correlationId },
            }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(
              FullCandidateActions.createOnePessimisticFailed({
                payload: { error, correlationId: payload.correlationId },
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FullCandidateActions.updateOnePessimistic),
      switchMap(({ payload }) =>
        this.fullCandidateHttpClient.update(payload.params).pipe(
          switchMap(({ candidate, references }) => [
            ...this.getReferencesActions(candidate, references),

            FullCandidateActions.updateOnePessimisticSuccess({ payload: { data: candidate } }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(
              FullCandidateActions.updateOnePessimisticFailed({
                payload: { error, data: { id: payload.params.data.id! } },
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public removePhoto$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FullCandidateActions.removePhoto),
      switchMap(({ payload }) =>
        this.fullCandidateHttpClient.removePhoto(payload.id).pipe(
          switchMap(({ candidate, references }) => [
            ...this.getReferencesActions(candidate, references),
            FullCandidateActions.removePhotoSuccess({ payload: { data: candidate } }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(FullCandidateActions.removePhotoFailed({ payload: { error, id: payload.id } })),
          ),
        ),
      ),
    ),
  );

  public updateCv$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FullCandidateActions.updateCv),
      switchMap(({ payload }) =>
        this.fullCandidateHttpClient.updateCv(payload).pipe(
          switchMap(({ candidate, references }) => [
            ...this.getReferencesActions(candidate, references),
            FullCandidateActions.updateCvSuccess({ payload: { data: candidate } }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(FullCandidateActions.updateCvFailed({ payload: { error, id: payload.id } })),
          ),
        ),
      ),
    ),
  );

  public updateParseCv$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FullCandidateActions.updateParseCv),
      switchMap(({ payload }) =>
        this.fullCandidateHttpClient.updateParseCv(payload).pipe(
          switchMap(({ candidate, references }) => [
            ...this.getReferencesActions(candidate, references),
            FullCandidateActions.updateParseCvSuccess({ payload: { data: candidate } }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(FullCandidateActions.updateParseCvFailed({ payload: { error, id: payload.id } })),
          ),
        ),
      ),
    ),
  );

  public deleteCv$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FullCandidateActions.deleteCv),
      switchMap(({ payload }) =>
        this.fullCandidateHttpClient.deleteCv(payload.id).pipe(
          switchMap(({ candidate, references }) => [
            ...this.getReferencesActions(candidate, references),
            FullCandidateActions.deleteCvSuccess({ payload: { data: candidate } }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(FullCandidateActions.deleteCvFailed({ payload: { error, id: payload.id } })),
          ),
        ),
      ),
    ),
  );

  public deleteParsedCv$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FullCandidateActions.deleteParsedCv),
      switchMap(({ payload }) =>
        this.fullCandidateHttpClient.deleteParsedCv(payload).pipe(
          switchMap(({ candidate, references }) => [
            ...this.getReferencesActions(candidate, references),
            FullCandidateActions.deleteParsedCvSuccess({ payload: { data: candidate } }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(FullCandidateActions.deleteParsedCvFailed({ payload: { error, id: payload.id } })),
          ),
        ),
      ),
    ),
  );

  public reparseCv$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FullCandidateActions.reparseCv),
      switchMap(({ payload }) =>
        this.fullCandidateHttpClient.reparseCv(payload.id).pipe(
          switchMap(({ candidate, references }) => [
            ...this.getReferencesActions(candidate, references),
            FullCandidateActions.reparseCvSuccess({ payload: { data: candidate } }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(FullCandidateActions.reparseCvFailed({ payload: { error, id: payload.id } })),
          ),
        ),
      ),
    ),
  );

  public view$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FullCandidateActions.view),
      switchMap(({ payload }) =>
        this.fullCandidateHttpClient.view(payload.id).pipe(
          switchMap(({ candidate, references }) => [
            ...this.getReferencesActions(candidate, references),
            FullCandidateActions.viewSuccess({ payload: { data: candidate } }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(FullCandidateActions.viewFailed({ payload: { error, id: payload.id } })),
          ),
        ),
      ),
    ),
  );

  public merge$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FullCandidateActions.merge),
      switchMap(({ payload }) =>
        this.fullCandidateHttpClient.merge(payload.id).pipe(
          switchMap(data => [FullCandidateActions.mergeSuccess({ payload: { uuid: data.uuid } })]),
          catchError((error: HttpErrorResponse) =>
            of(FullCandidateActions.mergeFailed({ payload: { error, id: payload.id } })),
          ),
        ),
      ),
    ),
  );

  public follow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FullCandidateActions.follow),
      switchMap(({ payload }) =>
        this.fullCandidateHttpClient.follow(payload.id).pipe(
          switchMap(({ candidate, references }) => [
            ...this.getReferencesActions(candidate, references),
            FullCandidateActions.followSuccess({ payload: { data: candidate } }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(FullCandidateActions.followFailed({ payload: { error, id: payload.id } })),
          ),
        ),
      ),
    ),
  );

  public unfollow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FullCandidateActions.unfollow),
      switchMap(({ payload }) =>
        this.fullCandidateHttpClient.unfollow(payload.id).pipe(
          switchMap(({ candidate, references }) => [
            ...this.getReferencesActions(candidate, references),
            FullCandidateActions.unfollowSuccess({ payload: { data: candidate } }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(FullCandidateActions.unfollowFailed({ payload: { error, id: payload.id } })),
          ),
        ),
      ),
    ),
  );

  public retrieve$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FullCandidateActions.retrieve),
      switchMap(({ payload }) =>
        this.fullCandidateHttpClient.retrieve(payload.id).pipe(
          switchMap(({ candidate, references }) => [
            ...this.getReferencesActions(candidate, references),
            FullCandidateActions.retrieveSuccess({ payload: { data: candidate } }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(FullCandidateActions.retrieveFailed({ payload: { error, id: payload.id } })),
          ),
        ),
      ),
    ),
  );

  public copyToCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FullCandidateActions.copyToCompany),
      switchMap(({ payload }) =>
        this.fullCandidateHttpClient.copyToCompany(payload.params).pipe(
          switchMap(({ candidate, references }) => [
            ...this.getReferencesActions(candidate, references),
            FullCandidateActions.copyToCompanySuccess({
              payload: { data: candidate, correlationId: payload.correlationId },
            }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(
              FullCandidateActions.copyToCompanyFailed({
                payload: { error, correlationId: payload.correlationId },
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public updateCoverLetterFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FullCandidateActions.updateCoverLetterFile),
      switchMap(({ payload }) =>
        this.fullCandidateHttpClient.updateCoverLetterFile(payload).pipe(
          switchMap(({ candidate, references }) => [
            ...this.getReferencesActions(candidate, references),
            FullCandidateActions.updateCoverLetterFileSuccess({
              payload: { data: candidate },
            }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(
              FullCandidateActions.updateCoverLetterFileFailed({
                payload: { error, id: payload.id },
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public deleteCoverLetterFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FullCandidateActions.deleteCoverLetterFile),
      switchMap(({ payload }) =>
        this.fullCandidateHttpClient.deleteCoverLetterFile(payload.candidateId).pipe(
          switchMap(({ candidate, references }) => [
            ...this.getReferencesActions(candidate, references),
            FullCandidateActions.deleteCoverLetterFileSuccess({
              payload: { data: candidate },
            }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(
              FullCandidateActions.deleteCoverLetterFileFailed({
                payload: { error, id: payload.candidateId },
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public shareWithTeam$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FullCandidateActions.shareWithTeam),
      switchMap(({ payload }) =>
        this.fullCandidateHttpClient.shareWithTeam(payload).pipe(
          withLatestFrom(
            this.store
              .select(UserSelectors.entities())
              .pipe(map(users => users.filter(user => payload.adminIds.includes(user.id)))),
          ),
          switchMap(([{ candidate }, users]) => [
            FullCandidateActions.shareWithTeamSuccess({
              payload: {
                ...candidate,
                id: payload.candidateId,
                sharedWith: users,
                newUsers: uniqByKey([...users, ...payload.admins], 'id'),
              },
            }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(FullCandidateActions.shareWithTeamFailed({ payload: { error } })),
          ),
        ),
      ),
    ),
  );

  public revokeAccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FullCandidateActions.revokeUserAccess),
      withLatestFrom(this.store.select(CurrentUserSelectors.data).pipe(filterDefined)),
      switchMap(([{ payload }, currentUser]) =>
        this.fullCandidateHttpClient.revokeAccess(payload.candidateId, payload.userId).pipe(
          switchMap(res => [
            // If we delete our own access, we need to fetch the candidate again to check if we still have access
            // recalculating access on FE might by tricky
            currentUser.id === payload.userId
              ? FullCandidateActions.fetchOne({ payload: { id: payload.candidateId } })
              : FullCandidateActions.revokeUserAccessSuccess({
                  payload: {
                    userId: payload.userId,
                    id: payload.candidateId,
                    ...res.candidate,
                    newUsers: payload.admins.filter(u => u.id !== payload.userId),
                  },
                }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(FullCandidateActions.revokeUserAccessFailed({ payload: { error } })),
          ),
        ),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private fullCandidateHttpClient: FullCandidateHttpClient,
    private store: Store,
  ) {}

  private getReferencesActions(candidate: FullCandidate, references: FullCandidateReferences[]) {
    const referenceActions = referencesToActionsFactory<FullCandidateReferences>()([
      { type: 'Admin', action: UserActions.references },
      { type: 'Stage', action: StageActions.references },
      { type: 'Offer', action: JobsBriefActions.references },
      { type: 'TalentPool', action: TalentPoolsNewBriefActions.references },
      { type: 'ReferralReferrer', action: CandidateReferrerActions.references },
    ]);

    const placementActions = candidate.placements?.length
      ? [PlacementActions.references({ payload: { data: candidate.placements } })]
      : [];

    return [...referenceActions(references), ...placementActions];
  }
}
